import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut } from "lucide-react";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "", role: "borrower" });
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { register, user, logout } = useAuth();
  const nav = useNavigate();

  const handleSwitchAccount = async () => {
    await logout();
    // Force a fresh state so the register form is clean.
    window.location.assign("/register");
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/verify-otp", { mobile: form.mobile || "0000000000", otp });
      setStep(3);
    } catch (err) {
      setError("Invalid OTP. Demo mode accepts 123456.");
    } finally { setLoading(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await register(form);
      toast.success("Account created");
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
      <h1 className="text-3xl font-bold text-slate-900 brand-heading" data-testid="register-heading">Register</h1>
      <p className="mt-2 text-sm text-slate-600">Create your Loan Bazaar account. Demo OTP is 123456.</p>

      {user && (
        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start sm:items-center gap-3 flex-col sm:flex-row" data-testid="register-signed-in-banner">
          <div className="flex-1 text-sm text-amber-900">
            You&apos;re currently signed in as <strong>{user.email}</strong>. Log out to register a new account.
          </div>
          <button
            onClick={handleSwitchAccount}
            data-testid="register-signout-btn"
            className="inline-flex items-center gap-2 rounded-md bg-amber-900 hover:bg-amber-800 text-white px-4 py-2 text-xs font-semibold"
          >
            <LogOut className="w-3.5 h-3.5" /> Log out & continue
          </button>
        </div>
      )}

      <div className="mt-6 p-8 rounded-lg border border-slate-200 bg-white">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6 text-xs">
          {[1, 2, 3].map((n, idx) => (
            <React.Fragment key={n}>
              <div className={`w-7 h-7 rounded-full grid place-items-center font-semibold ${step >= n ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>{n}</div>
              {idx < 2 && <div className={`h-0.5 flex-1 ${step > n ? "bg-slate-900" : "bg-slate-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(2); }} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-800">Applicant name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-800">Mobile</label>
                <input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="reg-mobile" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-800">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-800">Password</label>
                <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reg-password" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-800">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="reg-role" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900">
                  <option value="borrower">MSME Borrower</option>
                  <option value="facilitator">Facilitator</option>
                  <option value="bank_admin">Bank Administrator</option>
                  <option value="bank_rm">Bank Relationship Manager</option>
                </select>
              </div>
            </div>
            <button type="submit" data-testid="reg-next" className="rounded-md bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-semibold">Send OTP</button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitOtp} className="space-y-4">
            <p className="text-sm text-slate-700">An OTP has been sent to <strong>{form.mobile}</strong>. In demo mode, enter <strong className="font-mono">123456</strong>.</p>
            <input required value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit OTP" data-testid="reg-otp" className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
            {error && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep(1)} className="rounded-md border border-slate-300 px-4 py-2 text-sm">Back</button>
              <button type="submit" data-testid="reg-verify-otp" className="rounded-md bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 text-sm font-semibold">{loading ? "Verifying…" : "Verify OTP"}</button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={submitRegister} className="space-y-4">
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">OTP verified. Confirm and create account.</div>
            <div className="text-sm text-slate-700"><strong>Name:</strong> {form.name}</div>
            <div className="text-sm text-slate-700"><strong>Email:</strong> {form.email}</div>
            <div className="text-sm text-slate-700"><strong>Role:</strong> {form.role}</div>
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <input required type="checkbox" data-testid="reg-tos" className="mt-0.5" />
              <span>I have read and agree to the <Link to="/terms" className="underline">terms</Link> and <Link to="/privacy" className="underline">privacy policy</Link>. I understand that BharatKosh is not a lender or government authority.</span>
            </label>
            {error && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>}
            <button type="submit" disabled={loading} data-testid="reg-submit" className="rounded-md bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60">{loading ? "Creating…" : "Create account"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
